/**
 * EventPointMap — one place on a map, read only.
 *
 * The same OpenStreetMap tiles and the same Leaflet the organiser's
 * MapPointPicker uses, so nothing new is called and no key is needed. What is
 * different is that nothing here can be moved: a visitor is being shown where
 * the start line is, not asked to place it.
 *
 * Every interaction Leaflet offers by default is turned off — drag, scroll
 * zoom, double-click zoom, keyboard. A small map inside a scrolling page that
 * swallows the wheel is a trap: the page stops moving and the map zooms
 * instead. Hovering the pin names the place, which is the one thing a visitor
 * wants from a map this size.
 */

import React from 'react';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useResolvedPoint from './useResolvedPoint';

const ZOOM = 15;

/**
 * Leaflet's default marker points at image files it expects to resolve relative
 * to the CSS. Under a bundler they do not resolve and the marker renders as a
 * broken image — so the icon is drawn inline instead. No asset, nothing to 404.
 */
const PIN_ICON = L.divIcon({
  className: 'evb-mappin',
  html: '<span class="evb-mappin__dot" aria-hidden="true"></span>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

/**
 * With a line to show, the window is the whole route rather than one pin's
 * neighbourhood — otherwise the map is zoomed so far in that the line leaves
 * the frame in both directions and reads as a stray diagonal.
 */
function FitToLine({ line }) {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !line || line.length < 2 || typeof map.fitBounds !== 'function') return;
    map.fitBounds(line.map(p => [p.lat, p.lng]), { padding: [14, 14] });
  }, [map, line]);

  return null;
}

export default function EventPointMap({ point, label, title, line }) {
  // The stored coordinate if the route carries one; the address looked up if it
  // does not. Null while a lookup is in flight, and after one that found
  // nothing — either way the address below the map still reads.
  const resolved = useResolvedPoint(point, label);
  if (!resolved) return null;

  return (
    <div className="evb-map">
      <MapContainer
        className="evb-map__canvas"
        center={[resolved.lat, resolved.lng]}
        zoom={ZOOM}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
        zoomControl={false}
        attributionControl={false}
        aria-label={title || label}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // OSM's licence requires attribution wherever its tiles are shown.
          // The control is off because it does not fit a box this small, so the
          // credit is printed under the map instead — see .evb-map__credit.
          attribution="&copy; OpenStreetMap contributors"
        />
        {/* The route itself, when the organiser drew one. The pin stays: the
            line says where you run, the pin says where this cell's end of it
            is. */}
        {line && (
          <>
            <Polyline
              positions={line.map(p => [p.lat, p.lng])}
              pathOptions={{ color: '#12666C', weight: 4, opacity: 0.85 }}
            />
            <FitToLine line={line} />
          </>
        )}
        <Marker position={[resolved.lat, resolved.lng]} icon={PIN_ICON}>
          {label && <Tooltip direction="top" offset={[0, -10]}>{label}</Tooltip>}
        </Marker>
      </MapContainer>
      <p className="evb-map__credit">© OpenStreetMap contributors</p>
    </div>
  );
}
